/**
 * Timeline order for Details. The engine sorts by time; moves made inside one filter cascade share a
 * timestamp, so within a group of equal times the moves are re-ordered by following the chain — the
 * move whose "from" folder is no other move's "to" comes first, then the move leaving the folder the
 * previous one entered (`Inbox → Priority`, `Priority → Weak`, …). Other entries keep their place.
 * The chain follows the entries' folder ids, never their titles (a folder name may contain " → ").
 */

import type { TimelineEntry } from '@digester/core';

function ends(entry: TimelineEntry): { from: string; to: string } | null {
  if (entry.kind !== 'moved' || !entry.fromFolderId || !entry.toFolderId) return null;
  return { from: entry.fromFolderId, to: entry.toFolderId };
}

/** Order one group of moves that share a timestamp by from-folder → to-folder. */
export function chainMoves(moves: readonly TimelineEntry[]): TimelineEntry[] {
  if (moves.length < 2) return [...moves];
  const pending = moves.map((entry) => ({ entry, ends: ends(entry) }));
  const out: TimelineEntry[] = [];
  const targets = (list: typeof pending): Set<string> => new Set(list.map((x) => x.ends?.to).filter((x): x is string => x !== undefined));
  let current: string | null = null;
  while (pending.length) {
    let index: number = current === null ? -1 : pending.findIndex((x) => x.ends?.from === current);
    if (index < 0) {
      // Start (or restart) a chain: a move whose origin no remaining move enters.
      const entered = targets(pending);
      index = pending.findIndex((x) => x.ends !== null && !entered.has(x.ends.from));
      if (index < 0) index = 0;
    }
    const next: (typeof pending)[number] | undefined = pending.splice(index, 1)[0];
    if (!next) break;
    out.push(next.entry);
    current = next.ends?.to ?? null;
  }
  return out;
}

/** The engine's timeline with same-time cascades put in chain order. */
export function orderTimeline(entries: readonly TimelineEntry[]): TimelineEntry[] {
  const out: TimelineEntry[] = [];
  let i = 0;
  while (i < entries.length) {
    const at = entries[i]?.at;
    let j = i;
    while (j < entries.length && entries[j]?.at === at) j++;
    const group = entries.slice(i, j);
    const moves = group.filter((e) => ends(e) !== null);
    if (moves.length < 2) out.push(...group);
    else {
      const ordered = chainMoves(moves);
      let k = 0;
      for (const e of group) out.push(ends(e) !== null ? (ordered[k++] as TimelineEntry) : e);
    }
    i = j;
  }
  return out;
}
